import React from 'react'
import { useHistory } from 'react-router-dom'
import { createSelector } from 'reselect'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import {
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Fab,
  Box,
  TextField,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import esLocale from 'date-fns/locale/es'

import { locationsInfo, experiencesInfo } from '../../utils/searchInfo'

import './style.scss'

import {
  makeSelectSearchLocation,
  makeSelectSearchStartDate,
  makeSelectSearchEndDate,
  makeSelectSearchTipoExperiencia,
  set_search_location,
  set_search_end_date,
  set_search_tipo_experiencia,
  set_location,
  set_start_date,
  set_end_date,
  set_tipo_experiencia,
  set_search_start_date,
} from '../../redux/ducks/search'

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: '90%',
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  btnGrid: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

const stateSelector = createSelector(
  [
    makeSelectSearchLocation,
    makeSelectSearchStartDate,
    makeSelectSearchEndDate,
    makeSelectSearchTipoExperiencia,
  ],
  (searchLocation, searchStartDate, searchEndDate, searchTipoExperiencia) => ({
    searchLocation,
    searchStartDate,
    searchEndDate,
    searchTipoExperiencia,
  }),
)

const actionDispatch = (dispatch) => ({
  setSearchLocation: (searchLocation) =>
    dispatch(set_search_location(searchLocation)),
  setSearchStartDate: (searchStartDate) =>
    dispatch(set_search_start_date(searchStartDate)),
  setSearchEndDate: (searchEndDate) =>
    dispatch(set_search_end_date(searchEndDate)),
  setSearchTipoExperiencia: (searchTipoExperiencia) =>
    dispatch(set_search_tipo_experiencia(searchTipoExperiencia)),
  setLocation: (location) => dispatch(set_location(location)),
  setStartDate: (startDate) => dispatch(set_start_date(startDate)),
  setEndDate: (endDate) => dispatch(set_end_date(endDate)),
  setTipoExperiencia: (setTipoExperiencia) =>
    dispatch(set_tipo_experiencia(setTipoExperiencia)),
})

const Search = ({ isMock, size }) => {
  const history = useHistory()
  const classes = useStyles()

  const {
    searchLocation,
    searchStartDate,
    searchEndDate,
    searchTipoExperiencia,
  } = useSelector(stateSelector)

  const {
    setSearchLocation,
    setSearchStartDate,
    setSearchEndDate,
    setSearchTipoExperiencia,
    setLocation,
    setStartDate,
    setEndDate,
    setTipoExperiencia,
  } = actionDispatch(useDispatch())

  const handleSearch = () => {
    if (searchTipoExperiencia !== 'ALL') {
      setLocation(searchLocation)
      setTipoExperiencia(searchTipoExperiencia)
      setStartDate(searchStartDate)
      setEndDate(searchEndDate)
      history.push(`/search/${searchTipoExperiencia}`)
    } else {
      setLocation(searchLocation)
      setTipoExperiencia(searchTipoExperiencia)
      setStartDate(searchStartDate)
      setEndDate(searchEndDate)
      history.push('/')
    }
  }

  const localeMap = {
    es: esLocale,
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={localeMap.es}
    >
      <Box
        className="searchBar"
        borderRadius={60}
        py={size === 'small' ? 0 : 2}
        px={3}
      >
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={2}>
            <DatePicker
              disabled={isMock}
              className={classes.formControl}
              disableToolbar
              inputFormat="dd/MM/yyyy"
              id="desde-datepicker"
              label="Desde"
              value={searchStartDate ? new Date(searchStartDate) : null}
              onChange={(date) => {
                const options = {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }
                const nextDay = new Date(date).getTime() + 86400000
                setSearchStartDate(
                  new Date(date).toLocaleDateString('en-US', options),
                )
                setSearchEndDate(
                  new Date(nextDay).toLocaleDateString('en-US', options),
                )
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              minDate={new Date()}
              autoOk={true}
              renderInput={(props) => <TextField {...props} />}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              disabled={isMock}
              className={classes.formControl}
              disableToolbar
              inputFormat="dd/MM/yyyy"
              id="hasta-datepicker"
              label="Hasta"
              value={
                searchStartDate > searchEndDate
                  ? searchStartDate
                  : searchEndDate
              }
              onChange={(date) => {
                const options = {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }
                setSearchEndDate(
                  new Date(date).toLocaleDateString('en-US', options),
                )
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              minDate={searchStartDate}
              autoOk={true}
              renderInput={(props) => <TextField {...props} />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl className={classes.formControl}>
              <InputLabel shrink id="tipo-experiencia-label">
                Tipo de Experiencia
              </InputLabel>
              <Select
                disabled={isMock}
                labelId="tipo-experiencia-label"
                id="tipo-experiencia"
                value={searchTipoExperiencia}
                onChange={(event) =>
                  setSearchTipoExperiencia(event.target.value)
                }
              >
                {experiencesInfo.map((experiencia) => (
                  <MenuItem key={experiencia.value} value={experiencia.value}>
                    {experiencia.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1} className={classes.btnGrid}>
            <Fab
              size={size === 'small' ? 'small' : 'medium'}
              color="primary"
              aria-label="search"
              onClick={() => (isMock ? '' : handleSearch())}
            >
              <SearchIcon />
            </Fab>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  )
}

export default Search
